/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Moment, MomentVisibility } from '@app/app/page-modules/folio/models';
import { Page } from '../../../shared/models/page';
import { ObservableResult } from '../../../shared/store';
import { EnrichedTask, UserTaskStatus } from '../models/user-task.model';
import { MyHubDataService } from './data.service';

export class MockMyHubDataService implements MyHubDataService {
  getCommunityMoments(page: number, size: number): ObservableResult<Page<Moment>> {
    const pageMoments: Moment[] = [
      {
        _id: '1',
        title: 'Successfully completed Angular Advanced Course',
        likes: {
          liked: false,
          totalLikes: 15,
        },
        commentsCount: 3,
        totalViews: 42,
        tags: [
          {
            id: 'angular-tag',
            title: 'Angular',
            descendantsCount: 12,
          },
          {
            id: 'frontend-tag',
            title: 'Frontend Development',
            descendantsCount: 8,
          },
        ],
        content: [
          {
            uid: 'content-1',
            type: 'PARAGRAPH',
            content: {
              text: 'Just finished the Angular Advanced Course and learned so much about reactive programming and component architecture. The real-world projects really helped solidify my understanding of observables and state management.',
            },
          },
        ],
        createdAt: new Date('2024-01-20T10:30:00Z'),
        addedToFolio: true,
        visibility: MomentVisibility.Public,
        visible: true,
      },
      {
        _id: '2',
        title: 'Team collaboration breakthrough in sprint planning',
        likes: {
          liked: true,
          totalLikes: 23,
        },
        commentsCount: 7,
        totalViews: 89,
        tags: [
          {
            id: 'agile-tag',
            title: 'Agile Methodology',
            descendantsCount: 15,
          },
          {
            id: 'teamwork-tag',
            title: 'Teamwork',
            descendantsCount: 6,
          },
        ],
        content: [
          {
            uid: 'content-2',
            type: 'PARAGRAPH',
            content: {
              text: 'Our team had an amazing breakthrough in our sprint planning session today. We implemented a new estimation technique that improved our velocity predictions by 40%. The key was breaking down user stories into smaller, more manageable tasks.',
            },
          },
        ],
        createdAt: new Date('2024-01-18T14:15:00Z'),
        addedToFolio: false,
        visibility: MomentVisibility.Public,
        visible: true,
      },
      {
        _id: '3',
        title: 'Implementing design patterns in TypeScript',
        likes: {
          liked: false,
          totalLikes: 8,
        },
        commentsCount: 2,
        totalViews: 34,
        tags: [
          {
            id: 'typescript-tag',
            title: 'TypeScript',
            descendantsCount: 20,
          },
          {
            id: 'design-patterns-tag',
            title: 'Design Patterns',
            descendantsCount: 11,
          },
        ],
        content: [
          {
            uid: 'content-3',
            type: 'PARAGRAPH',
            content: {
              text: 'Spent the week refactoring our codebase to implement proper design patterns. The Observer pattern has been particularly useful for handling state changes across components. Clean code principles really make a difference in maintainability.',
            },
          },
        ],
        createdAt: new Date('2024-01-15T16:45:00Z'),
        addedToFolio: true,
        visibility: MomentVisibility.Public,
        visible: true,
      },
      {
        _id: '4',
        title: 'Machine Learning workshop insights',
        likes: {
          liked: true,
          totalLikes: 31,
        },
        commentsCount: 12,
        totalViews: 156,
        tags: [
          {
            id: 'ml-tag',
            title: 'Machine Learning',
            descendantsCount: 18,
          },
          {
            id: 'python-tag',
            title: 'Python',
            descendantsCount: 25,
          },
        ],
        content: [
          {
            uid: 'content-4',
            type: 'PARAGRAPH',
            content: {
              text: "Attended an incredible ML workshop focusing on neural networks and deep learning. The hands-on exercises with TensorFlow opened up so many possibilities for our upcoming projects. Can't wait to apply these concepts to real-world problems.",
            },
          },
        ],
        createdAt: new Date('2024-01-12T09:20:00Z'),
        addedToFolio: true,
        visibility: MomentVisibility.Public,
        visible: true,
      },
      {
        _id: '5',
        title: 'Successful deployment to production',
        likes: {
          liked: false,
          totalLikes: 19,
        },
        commentsCount: 5,
        totalViews: 67,
        tags: [
          {
            id: 'devops-tag',
            title: 'DevOps',
            descendantsCount: 14,
          },
          {
            id: 'deployment-tag',
            title: 'Deployment',
            descendantsCount: 9,
          },
        ],
        content: [
          {
            uid: 'content-5',
            type: 'PARAGRAPH',
            content: {
              text: 'Successfully deployed our new feature to production with zero downtime! The CI/CD pipeline we built over the past months really paid off. Automated testing caught several edge cases that would have been critical bugs.',
            },
          },
        ],
        createdAt: new Date('2024-01-10T11:00:00Z'),
        addedToFolio: false,
        visibility: MomentVisibility.Public,
        visible: true,
      },
    ];

    const startIndex = page * size;
    const endIndex = startIndex + size;
    const paginatedMoments = pageMoments.slice(startIndex, endIndex);

    const pageResult: Page<Moment> = {
      content: paginatedMoments,
      totalNumberOfElement: pageMoments.length,
      pageSize: size,
      currentPageNumber: page,
      numberOfPages: Math.ceil(pageMoments.length / size),
      hasContent: paginatedMoments.length > 0,
    };

    return ObservableResult.ofSuccess(pageResult);
  }

  getTasks(page: number, size: number): ObservableResult<Page<EnrichedTask>> {
    const mockTasks: EnrichedTask[] = [
      {
        id: 'U4VqFQXG3jI0Y6IU',
        organizationUid: '607c7fea08d83a0f46ef76ac',
        type: 'CANVAS',
        title: 'Add Digital Proficiency to my Skills Wallet',
        contentType: 'CANVAS_EXTERNAL_TOOL',
        mandatory: true,
        dueDate: null,
        createdAt: '2025-10-06T09:32:32.734',
        updatedAt: '2025-10-06T09:32:32.734',
        canvasCourseId: 9,
        canvasAssignmentId: 32,
        courseName: 'Digital Proficiency',
        canvasUrl: 'https://canvas.potential.ly/courses/9/assignments/32',
        status: UserTaskStatus.TODO,
      },
      {
        id: 'pnwrUyu7wxB3spz6',
        organizationUid: '607c7fea08d83a0f46ef76ac',
        type: 'CANVAS',
        title: 'Complete Programming Assignment',
        contentType: 'CANVAS_ONLINE_UPLOAD',
        mandatory: true,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: '2025-10-06T09:32:32.936',
        updatedAt: '2025-10-06T09:32:32.936',
        canvasCourseId: 9,
        canvasAssignmentId: 34,
        courseName: 'Digital Proficiency',
        canvasUrl: 'https://canvas.potential.ly/courses/9/assignments/34',
        status: UserTaskStatus.TODO,
      },
      {
        id: 'quiz123ABC',
        organizationUid: '607c7fea08d83a0f46ef76ac',
        type: 'CANVAS',
        title: 'HTML & CSS Quiz',
        contentType: 'CANVAS_ONLINE_QUIZ',
        mandatory: true,
        dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: '2025-10-08T14:20:15.123',
        updatedAt: '2025-10-08T14:20:15.123',
        canvasCourseId: 15,
        canvasAssignmentId: 45,
        courseName: 'Web Development Fundamentals',
        canvasUrl: 'https://canvas.potential.ly/courses/15/assignments/45',
        status: UserTaskStatus.TODO,
      },
      {
        id: 'discussion789',
        organizationUid: '607c7fea08d83a0f46ef76ac',
        type: 'CANVAS',
        title: 'Design Patterns Discussion',
        contentType: 'CANVAS_DISCUSSION_TOPIC',
        mandatory: false,
        dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: '2025-10-09T10:15:30.456',
        updatedAt: '2025-10-09T10:15:30.456',
        canvasCourseId: 22,
        canvasAssignmentId: 67,
        courseName: 'Software Engineering Principles',
        canvasUrl: 'https://canvas.potential.ly/courses/22/assignments/67',
        status: UserTaskStatus.TODO,
      },
      {
        id: 'passfail456',
        organizationUid: '607c7fea08d83a0f46ef76ac',
        type: 'CANVAS',
        title: 'Resume Review Workshop',
        contentType: 'CANVAS_ON_PAPER',
        mandatory: false,
        dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: '2025-10-07T16:45:22.789',
        updatedAt: '2025-10-07T16:45:22.789',
        canvasCourseId: 18,
        canvasAssignmentId: 89,
        courseName: 'Professional Development',
        canvasUrl: 'https://canvas.potential.ly/courses/18/assignments/89',
        status: UserTaskStatus.COMPLETED,
      },
    ];

    const startIndex = page * size;
    const endIndex = startIndex + size;
    const paginatedTasks = mockTasks.slice(startIndex, endIndex);

    const pageResult: Page<EnrichedTask> = {
      content: paginatedTasks,
      totalNumberOfElement: mockTasks.length,
      pageSize: size,
      currentPageNumber: page,
      numberOfPages: Math.ceil(mockTasks.length / size),
      hasContent: paginatedTasks.length > 0,
    };

    return ObservableResult.ofSuccess(pageResult);
  }
}
